/**
 * Валидация полей входа (паритет с `frontend/admin` AuthScreen) и согласованно во всех экранах embed-виджета.
 */

import { normalizePhoneRu, RU_PHONE_DIGITS, RU_ZONE_PHONE_PLAUSIBLE } from './phone';

export type ValidateResult = true | string;

/** Минимальная длина нового пароля (политика API: 12 символов + разные классы). */
export const EMBED_MIN_PASSWORD_LENGTH = 12;

export function validateEmail(value: string, opts?: { required?: boolean }): ValidateResult {
  const v = (value ?? '').trim().toLowerCase();
  if (!v) return opts?.required ? 'Укажите email.' : true;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Некорректный формат email.';
  if (/\.{2,}/.test(v)) return 'Email не может содержать две точки подряд.';
  return true;
}

export function validatePhoneRu(value: string, opts: { required: boolean }): ValidateResult {
  const raw = (value ?? '').trim();
  const digitsOnly = raw.replace(/\D/g, '');
  if (!digitsOnly || digitsOnly === '7' || digitsOnly === '8') {
    return opts.required ? 'Укажите телефон (10 цифр после +7).' : true;
  }
  const n = normalizePhoneRu(raw);
  if (n.length !== RU_PHONE_DIGITS || !n.startsWith('7')) {
    return 'Телефон: укажите полный российский номер (11 цифр, код страны 7).';
  }
  if (!RU_ZONE_PHONE_PLAUSIBLE.test(n)) {
    return 'Укажите корректный номер телефона в формате +7.';
  }
  return true;
}

/**
 * Пусто — ок; непусто — полный валидный email (для полей вроде «email необязателен»).
 */
export function validateEmailIfPresent(value: string): ValidateResult {
  const t = (value ?? '').trim();
  if (!t) return true;
  return validateEmail(t, { required: true });
}

/** Новый пароль при регистрации / сбросе / смене. */
export function validateNewPassword(value: string, opts?: { required?: boolean; minLength?: number }): ValidateResult {
  const min = opts?.minLength ?? EMBED_MIN_PASSWORD_LENGTH;
  const v = value ?? '';
  if (!v.trim()) return opts?.required ? 'Укажите пароль.' : true;
  if (v.length < min) return `Минимум ${min} символов.`;
  return true;
}

/**
 * Пароль по политике API (set-initial-password и аналогичные):
 * ≥12 символов, минимум одна строчная латинская, одна прописная и одна цифра.
 */
export function validatePasswordPolicy(value: string, opts?: { required?: boolean }): ValidateResult {
  const v = value ?? '';
  if (!v) return opts?.required ? 'Укажите пароль.' : true;
  if (v.length < EMBED_MIN_PASSWORD_LENGTH) return `Минимум ${EMBED_MIN_PASSWORD_LENGTH} символов.`;
  if (!/[a-z]/.test(v)) return 'Добавьте хотя бы одну строчную букву.';
  if (!/[A-Z]/.test(v)) return 'Добавьте хотя бы одну прописную букву.';
  if (!/[0-9]/.test(v)) return 'Добавьте хотя бы одну цифру.';
  return true;
}

export function validateFullName(value: string, opts?: { required?: boolean }): ValidateResult {
  const v = (value ?? '').trim();
  if (!v) return opts?.required ? 'Укажите ФИО.' : true;
  const parts = v.split(/\s+/);
  if (parts.length < 2) {
    return 'Укажите фамилию и имя через пробел.';
  }
  if (!/^[a-zA-Zа-яА-ЯёЁ\s-]+$/.test(v)) {
    return 'ФИО может содержать только буквы, пробелы и дефисы.';
  }
  for (const part of parts) {
    if (part.replace(/-/g, '').length < 2) {
      return 'Каждое слово в ФИО должно быть не короче 2 букв.';
    }
  }
  return true;
}

export function validateDateOfBirth(value: string, opts?: { required?: boolean }): ValidateResult {
  const v = (value ?? '').trim();
  if (!v) return opts?.required ? 'Укажите дату рождения.' : true;
  const dateObj = new Date(v);
  if (isNaN(dateObj.getTime())) {
    return 'Некорректный формат даты.';
  }
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const birthDate = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
  if (birthDate > today) {
    return 'Дата рождения не может быть в будущем.';
  }
  if (today.getFullYear() - birthDate.getFullYear() > 120) {
    return 'Укажите корректную дату рождения.';
  }
  return true;
}
